import React, { useState, useRef } from "react";
import axios from "axios";
import Recorder from "recorder-js";
import { FaMicrophone, FaStop, FaPaperPlane, FaSave, FaUpload } from "react-icons/fa";
import Navbar from "./Navbar";
import { useLocation } from "react-router-dom";

const DoctorVoicePrescription = () => {
  const location = useLocation();

  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [prescriptionText, setPrescriptionText] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedLang, setSelectedLang] = useState("en");

  const recorderRef = useRef(null);
  const audioContextRef = useRef(null);

  const languages = {
    en: "English",
    hi: "Hindi",
    mr: "Marathi",
    te: "Telugu",
    ta: "Tamil",
    bn: "Bengali",
    gu: "Gujarati",
    pa: "Punjabi"
  };

  // 🎙️ Start recording
  const startRecording = async () => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    recorderRef.current = new Recorder(audioContextRef.current);

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    await recorderRef.current.init(stream);
    recorderRef.current.start();

    setIsRecording(true);
  };

  // 🎙️ Stop recording
  const stopRecording = async () => {
    const { blob } = await recorderRef.current.stop();

    const url = URL.createObjectURL(blob);
    setAudioUrl(url);

    setIsRecording(false);
  };

  // 🚀 Send audio for transcription
  const handleSendAudio = async () => {
    if (!audioUrl) return;

    setLoading(true);

    const audioBlob = await fetch(audioUrl).then((res) => res.blob());

    const formData = new FormData();
    formData.append("file", audioBlob, "doctor_voice_note.wav");
    formData.append("lang", selectedLang); // ✅ send language code

    try {
      const response = await axios.post(
        "https://www.careplus.gopillz.com/voice_to_text",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setPrescriptionText(response.data.text);

    } catch (error) {
      console.error(error);
      alert("Transcription failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // 📂 Select file
  const handleFileChange = (e) => {
    setUploadedFile(e.target.files[0]);
  };

  // 📂 Upload prescription
  const handleUploadPrescription = async () => {

    if (!uploadedFile) {
      alert("Please select a file first.");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("files", uploadedFile);
    formData.append("lang", selectedLang); // ✅ send language code

    try {

      const response = await axios.post(
        "https://lovencare.gopillz.com/api/doctor_prescription_analyze",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      alert("Prescription uploaded successfully!");

      setPrescriptionText(response.data.explanation);
      setUploadedFile(null);

    } catch (error) {

      console.error(error);
      alert("Failed to upload prescription.");

    } finally {
      setUploading(false);
    }
  };

  // ✅ Submit prescription
  const handleSubmitPrescription = async () => {

    if (!prescriptionText.trim()) {
      alert("Prescription text cannot be empty.");
      return;
    }

    const patient_id = location.state?.patient_id;

    const headersList = {
      Accept: "*/*",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      "Content-Type": "application/json",
    };

    setSubmitting(true);

    try {

      await axios.post(
        "https://www.careplus.gopillz.com/speak_generate_prescription",
        {
          doctor_note: prescriptionText,
          id: patient_id,
          lang: selectedLang // ✅ send language code
        },
        { headers: headersList }
      );

      alert("Prescription submitted successfully!");

      setPrescriptionText("");
      setAudioUrl("");

    } catch (error) {

      console.error(error);
      alert("Failed to submit prescription.");

    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="min-h-screen flex flex-col items-center bg-gradient-to-br from-emerald-50 to-blue-100 p-6 mt-20">

        <h1 className="text-3xl font-bold mb-6 text-blue-800">
          🎙️ Doctor Voice Prescription
        </h1>

        <div className="flex flex-col items-center space-y-6 bg-white p-8 rounded-2xl shadow-2xl max-w-lg w-full">

          {/* 🌐 Language selection */}

          <div className="w-full flex flex-col space-y-2">

            <label className="text-gray-700 font-semibold">
              🌐 Choose Language:
            </label>

            <select
              value={selectedLang}
              onChange={(e) => setSelectedLang(e.target.value)}
              className="border border-gray-300 rounded-lg p-2 w-full"
            >
              {Object.entries(languages).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>

            <p className="text-sm text-gray-600 mt-1">
              Reason: The prescription will be sent to the patient in
              <span className="font-semibold"> {languages[selectedLang]}</span> via a voice note.
            </p>

          </div>

          {/* 🎙️ Recording */}

          {isRecording ? (
            <button
              onClick={stopRecording}
              className="flex items-center gap-2 px-6 py-3 rounded-full text-white bg-red-600 hover:bg-red-700 shadow-md"
            >
              <FaStop /> Stop Recording
            </button>
          ) : (
            <button
              onClick={startRecording}
              className="flex items-center gap-2 px-6 py-3 rounded-full text-white bg-green-600 hover:bg-green-700 shadow-md"
            >
              <FaMicrophone /> Start Recording
            </button>
          )}

          {/* 🎧 Audio playback */}

          {audioUrl && (
            <div className="flex flex-col items-center w-full space-y-4">

              <audio controls src={audioUrl} className="w-full rounded" />

              <button
                onClick={handleSendAudio}
                disabled={loading}
                className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white w-full ${
                  loading ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                <FaPaperPlane />
                {loading ? "Transcribing..." : "Transcribe Audio"}
              </button>

            </div>
          )}

          {/* 📂 Upload prescription */}

          <div className="w-full flex flex-col space-y-3">

            <label className="text-gray-700 font-semibold">
              📂 Upload Prescription File:
            </label>

            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.wav,.mp3"
              onChange={handleFileChange}
              className="border border-gray-300 rounded-lg p-2"
            />

            <button
              onClick={handleUploadPrescription}
              disabled={uploading}
              className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white ${
                uploading ? "bg-gray-400" : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              <FaUpload />
              {uploading ? "Uploading..." : "Upload Prescription"}
            </button>

          </div>

          {/* ✍️ Prescription editor */}

          <div className="w-full">

            <label className="text-gray-700 font-semibold mb-2">
              ✍️ Write/Edit Prescription:
            </label>

            <textarea
              rows="6"
              placeholder="Write the prescription here..."
              value={prescriptionText}
              onChange={(e) => setPrescriptionText(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-4 focus:ring-2 focus:ring-blue-400 resize-none"
            />

          </div>

          {/* ✅ Submit */}

          <button
            onClick={handleSubmitPrescription}
            disabled={submitting}
            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white w-full ${
              submitting ? "bg-gray-400" : "bg-purple-600 hover:bg-purple-700"
            }`}
          >
            <FaSave />
            {submitting ? "Submitting..." : "Submit Prescription"}
          </button>

        </div>

      </div>
    </div>
  );
};

export default DoctorVoicePrescription;